import type { KeyboardEvent } from 'react';
import { getVenuePrimarySport } from 'constants/venues';
import { CardLoadingSkeleton } from 'components/DiscoverPage/utils';
import CardVenue from 'components/cards/CardVenue';
import type { AddVenueState } from '../useAddVenue';

const SKELETON_COUNT = 4;
const GRID_CLASS = 'grid grid-cols-1 gap-x-ds-xl gap-y-ds-2xl lg:grid-cols-2';

type Props = Pick<
  AddVenueState,
  'venues' | 'isVenuesLoading' | 'hasSearchCenter' | 'selectedSlug' | 'selectVenue'
>;

export default function VenueResults({
  venues,
  isVenuesLoading,
  hasSearchCenter,
  selectedSlug,
  selectVenue,
}: Props) {
  if (isVenuesLoading && venues.length === 0) {
    return (
      <ul className={GRID_CLASS}>
        {Array.from({ length: SKELETON_COUNT }, (_, index) => (
          <li key={index}>
            <CardLoadingSkeleton />
          </li>
        ))}
      </ul>
    );
  }

  if (venues.length === 0) {
    return (
      <p className="typography-product-caption py-ds-3xl text-center text-color-text-lightmode-placeholder dark:text-color-text-darkmode-placeholder">
        {hasSearchCenter
          ? 'No locations found nearby. Try searching a different area.'
          : 'Search a location to see courts nearby.'}
      </p>
    );
  }

  return (
    <ul className={GRID_CLASS}>
      {venues.map((venue) => {
        const handleKeyDown = (e: KeyboardEvent) => {
          if (e.key !== 'Enter' && e.key !== ' ') return;
          e.preventDefault();
          selectVenue(venue.slug);
        };
        return (
          <li key={venue.id}>
            <div
              role="button"
              tabIndex={0}
              aria-pressed={venue.slug === selectedSlug}
              className="cursor-pointer rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-color-text-lightmode-primary focus-visible:ring-offset-8 dark:focus-visible:ring-color-text-darkmode-primary"
              onClick={() => selectVenue(venue.slug)}
              onKeyDown={handleKeyDown}
            >
              <CardVenue
                isLink={false}
                isSelected={venue.slug === selectedSlug}
                id={venue.id}
                slug={venue.slug}
                imageUrl={venue.images?.[0]?.url}
                title={venue.title}
                addressString={venue.addressString}
                accessType={venue.accessType}
                totalCourtCount={venue.indoorCourtCount + venue.outdoorCourtCount}
                pickleballNets={venue.pickleballNets}
                primarySport={getVenuePrimarySport(venue)}
              />
            </div>
          </li>
        );
      })}
    </ul>
  );
}
